import { Alert, Box, Button } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEntitySecurityPath } from '../../../../auth/Security';
import { useDeepLinkURL } from '../../../../deepLink';
import I18n from '../../../../i18n/I18n';
import type { ShareableEntity } from '../../../../share';
import { ShareSettings, useShareSettings } from '../../../../share';
import { SHARE_SETTINGS_MODE } from '../../../../share/constants';
import { shareSettingsDataToShareSettingsViewModel } from '../../../../share/data/utils';
import { successToastMessage } from '../../../../utils/commonUtils';
import { usePanelCommands } from '../../../ui/hooks/usePanelCommands';
import { usePanelSession } from '../../../ui/hooks/usePanelSession';
import type { SharePanelOptions, ShareSavedInput } from '../types';

const FOOTER_SX = { display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 };

export function SharePanelSettings({
    entity,
    deepLinkUIStatePath,
    scopePrefix = 'share'
}: SharePanelOptions & { entity: ShareableEntity }) {
    const { subject, sessionId } = usePanelSession();
    const { close, dispatch } = usePanelCommands();
    const { saveShareSettings, checkHasUnsavedShareSettings, isSavingShareSettings } = useShareSettings();
    const { getDeepLinkURL } = useDeepLinkURL();
    const entitySecurityPath = useEntitySecurityPath();
    const [hasSaveError, setHasSaveError] = useState(false);
    const isActive = useRef(false);
    const isSaving = useRef(false);
    const scopeId = `${scopePrefix}:${subject?.entityName}:${subject?.reference.id}:${sessionId}`;
    const initialShareSettings = useMemo(
        () =>
            shareSettingsDataToShareSettingsViewModel({
                restricted: entity.restricted,
                sharedWithAccounts: entity.sharedWithAccounts ?? [],
                sharedWithDepartments: entity.sharedWithDepartments ?? [],
                entityCreatedBy: entity.createdBy,
                entityCreatedByName: entity.createdByName
            }),
        [entity]
    );

    useEffect(() => {
        isActive.current = true;
        return () => {
            isActive.current = false;
        };
    }, []);

    const handleClose = useCallback(() => {
        close();
    }, [close]);
    const handleSave = useCallback(async () => {
        if (!subject?.reference.id || isSaving.current) return;
        isSaving.current = true;
        setHasSaveError(false);
        try {
            const deepLinkURL = getDeepLinkURL(`${deepLinkUIStatePath ?? entitySecurityPath}.id`);
            await saveShareSettings(scopeId, { id: subject.reference.id }, deepLinkURL);
            if (!isActive.current) return;
            const result = await dispatch<ShareSavedInput>('share.saved', {
                entityId: subject.reference.id,
                entityName: subject.entityName
            });
            if (!isActive.current || result.status === 'superseded') return;
            successToastMessage('common.share.settings.update.success');
            await close();
        } catch {
            if (isActive.current) setHasSaveError(true);
        } finally {
            isSaving.current = false;
        }
    }, [close, deepLinkUIStatePath, dispatch, entitySecurityPath, getDeepLinkURL, saveShareSettings, scopeId, subject]);

    return (
        <>
            <ShareSettings
                mode={SHARE_SETTINGS_MODE.update}
                scopeId={scopeId}
                initialShareSettings={initialShareSettings}
            />
            {hasSaveError && (
                <Alert severity="error">
                    <I18n token="common.share.settings.update.failure" />
                </Alert>
            )}
            <Box sx={FOOTER_SX}>
                <Button onClick={handleClose} disabled={isSavingShareSettings} variant="text">
                    <I18n token="dialog.cancel" />
                </Button>
                <Button
                    data-testid="share-panel-save"
                    onClick={handleSave}
                    disabled={!checkHasUnsavedShareSettings(scopeId) || isSavingShareSettings}
                    variant="contained"
                >
                    <I18n token="form.button.save" />
                </Button>
            </Box>
        </>
    );
}
